// src/services/tickets.ts
/**
 * Service para operaciones de Tickets.
 */
import { api, getAccessToken } from './api';
import type {
  Ticket,
  TicketCreate,
  TicketUpdate,
  TicketFiltros,
  TicketEstadoUpdate,
  TicketAsignar,
  TicketComentario,
  ComentarioCreate,
  TicketAdjunto,
  TicketHistorial,
} from '@/types';

export const ticketsService = {
  // Tickets CRUD
  list: (params?: TicketFiltros) => {
    const searchParams = new URLSearchParams();
    if (params?.tipo) searchParams.set('tipo', params.tipo);
    if (params?.estado) searchParams.set('estado', params.estado);
    if (params?.prioridad) searchParams.set('prioridad', params.prioridad);
    if (params?.cliente_id) searchParams.set('cliente_id', params.cliente_id);
    if (params?.asignado_a_id) searchParams.set('asignado_a_id', params.asignado_a_id);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.fecha_desde) searchParams.set('fecha_desde', params.fecha_desde);
    if (params?.fecha_hasta) searchParams.set('fecha_hasta', params.fecha_hasta);

    const query = searchParams.toString();
    return api.get<Ticket[]>(`/tickets${query ? `?${query}` : ''}`);
  },

  get: (id: string) => api.get<Ticket>(`/tickets/${id}`),

  create: (data: TicketCreate) => api.post<Ticket>('/tickets', data),

  update: (id: string, data: TicketUpdate) => api.patch<Ticket>(`/tickets/${id}`, data),

  delete: (id: string) => api.delete<void>(`/tickets/${id}`),

  cambiarEstado: (id: string, data: TicketEstadoUpdate) =>
    api.patch<Ticket>(`/tickets/${id}/estado`, data),

  asignar: (id: string, data: TicketAsignar) =>
    api.patch<Ticket>(`/tickets/${id}/asignar`, data),

  // Comentarios
  getComentarios: (ticketId: string) =>
    api.get<TicketComentario[]>(`/tickets/${ticketId}/comentarios`),

  createComentario: (ticketId: string, data: ComentarioCreate) =>
    api.post<TicketComentario>(`/tickets/${ticketId}/comentarios`, data),

  deleteComentario: (ticketId: string, comentarioId: string) =>
    api.delete<void>(`/tickets/${ticketId}/comentarios/${comentarioId}`),

  // Adjuntos
  getAdjuntos: (ticketId: string) =>
    api.get<TicketAdjunto[]>(`/tickets/${ticketId}/adjuntos`),

  uploadAdjunto: async (ticketId: string, file: File): Promise<TicketAdjunto> => {
    const formData = new FormData();
    formData.append('file', file);

    const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
    const token = getAccessToken();

    const response = await fetch(`${API_BASE_URL}/tickets/${ticketId}/adjuntos`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Error al subir archivo');
    }

    return response.json();
  },

  downloadAdjunto: async (ticketId: string, adjuntoId: string): Promise<Blob> => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
    const token = getAccessToken();

    const response = await fetch(
      `${API_BASE_URL}/tickets/${ticketId}/adjuntos/${adjuntoId}/descargar`,
      {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error('Error al descargar archivo');
    }

    return response.blob();
  },

  deleteAdjunto: (ticketId: string, adjuntoId: string) =>
    api.delete<void>(`/tickets/${ticketId}/adjuntos/${adjuntoId}`),

  // Historial
  getHistorial: (ticketId: string) =>
    api.get<TicketHistorial[]>(`/tickets/${ticketId}/historial`),
};
